import { useCallback, useEffect, useState } from 'react';

export type UseCamerasResult = {
  cameras: MediaDeviceInfo[];
  ready: boolean;
  refresh: () => Promise<MediaDeviceInfo[]>;
};

export type UseCamerasOptions = {
  /** When true, requests camera permission and listens for device changes. */
  enabled?: boolean;
};

async function enumerateVideoDevices(): Promise<MediaDeviceInfo[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((device) => device.kind === 'videoinput');
}

const useCameras = ({ enabled = false }: UseCamerasOptions = {}): UseCamerasResult => {
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      const videoDevices = await enumerateVideoDevices();
      setCameras(videoDevices);
      setReady(true);
      return videoDevices;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch {
      // Permission denied or unavailable — still enumerate what we can.
    }

    const videoDevices = await enumerateVideoDevices();
    setCameras(videoDevices);
    setReady(true);
    return videoDevices;
  }, []);

  useEffect(() => {
    if (!enabled) {
      setReady(false);
      return undefined;
    }

    void refresh();
    const onDeviceChange = () => {
      void refresh();
    };
    navigator.mediaDevices?.addEventListener('devicechange', onDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', onDeviceChange);
    };
  }, [enabled, refresh]);

  return { cameras, ready, refresh };
};

export default useCameras;
